import React, { useState, useMemo } from "react";
import {
  ChevronDown, ArrowUp, ArrowDown, MoreHorizontal, Inbox,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/* 1. PageHeader --------------------------------------------------------- */
export function PageHeader({ title, summary, actions, testid }) {
  return (
    <div className="flex items-start justify-between gap-4 pt-11 pb-6" data-testid={testid}>
      <div>
        <h1 className="font-display text-[28px] leading-[34px] font-bold text-nb-ink">{title}</h1>
        {summary && <p className="text-[15px] text-nb-ink2 mt-1 max-w-[720px]">{summary}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
    </div>
  );
}

/* 2. Tabs (underline) --------------------------------------------------- */
export function Tabs({ tabs, value, onChange, testid = "tabs" }) {
  return (
    <div className="flex items-center gap-6 h-11 border-b" style={{ borderColor: "var(--border-subtle)" }} data-testid={testid}>
      {tabs.map((t) => {
        const active = t.key === value;
        return (
          <button
            key={t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => onChange(t.key)}
            className="relative h-11 inline-flex items-center gap-2 text-[14px] transition-colors"
            style={{ color: active ? "var(--brand-navy)" : "var(--text-muted)", fontWeight: active ? 600 : 500 }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.color = "var(--brand-navy)"; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = "var(--text-muted)"; }}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className="text-[11px] font-semibold rounded-md px-1.5 py-0.5 tnum"
                style={{ background: "var(--bg-sunken)", color: "var(--brand-navy)" }}>{t.count}</span>
            )}
            {active && <span className="absolute left-0 right-0 -bottom-px h-0.5" style={{ background: "var(--brand-navy)" }} />}
          </button>
        );
      })}
    </div>
  );
}

/* 3. SegmentedControl --------------------------------------------------- */
export function SegmentedControl({ options, value, onChange, testid = "segmented" }) {
  return (
    <div className="inline-flex items-center gap-1 p-1 rounded-[10px]" style={{ background: "var(--bg-sunken)" }} data-testid={testid}>
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            data-testid={`${testid}-${o.value}`}
            onClick={() => onChange(o.value)}
            className="h-[34px] px-3 rounded-md text-[13px] font-medium transition-colors"
            style={active
              ? { background: "var(--brand-navy)", color: "var(--text-inverse)" }
              : { background: "transparent", color: "var(--brand-navy)" }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/* 5. QuickFilterChips --------------------------------------------------- */
export function QuickFilterChips({ chips, value, onChange, testid = "quick-chips" }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      {chips.map((c) => {
        const active = c.key === value;
        return (
          <button
            key={c.key}
            data-testid={`chip-${c.key}`}
            onClick={() => onChange(c.key)}
            className="h-8 px-3 rounded-md text-[13px] font-medium inline-flex items-center gap-1.5 transition-colors"
            style={active
              ? { background: "var(--brand-navy)", color: "var(--text-inverse)" }
              : { background: "var(--bg-surface)", color: "var(--brand-navy)", border: "1px solid var(--border-subtle)" }}
          >
            {c.label}
            {c.count != null && <span className="tnum opacity-80">{c.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* 7. StatusBadge + PriorityBadge --------------------------------------- */
const STATUS = {
  "High usage": { dot: "var(--brand-navy)", arrow: null },
  "Moderate usage": { dot: "var(--brand-kawaii)", arrow: null },
  "Low usage": { dot: "var(--brand-floral)", arrow: "▼" },
  "Too early to tell": { dot: "var(--text-muted)", arrow: null },
};
export function StatusBadge({ status, testid }) {
  const s = STATUS[status] || STATUS["Too early to tell"];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[13px] font-semibold text-nb-ink"
      style={{ background: "var(--bg-sunken)" }} data-testid={testid}>
      <span className="w-2 h-2 rounded-full" style={{ background: s.dot }} />
      {status}{s.arrow ? <span style={{ color: "var(--brand-floral)" }}>{s.arrow}</span> : null}
    </span>
  );
}

const PRIORITY = {
  High: { background: "var(--brand-navy)", color: "var(--text-inverse)", border: "none" },
  Medium: { background: "var(--bg-sunken)", color: "var(--brand-navy)", border: "none" },
  Low: { background: "var(--bg-surface)", color: "var(--brand-navy)", border: "1px solid var(--border-subtle)" },
};
export function PriorityBadge({ priority, testid }) {
  const p = PRIORITY[priority] || PRIORITY.Medium;
  return (
    <span className="inline-flex items-center justify-center rounded-md px-2 py-1 text-[13px] font-semibold"
      style={{ background: p.background, color: p.color, border: p.border, minWidth: 62 }} data-testid={testid}>
      {priority}
    </span>
  );
}

/* 8. DeltaBadge --------------------------------------------------------- */
export function DeltaBadge({ change, higherIsBetter = true, period = "vs last 30 days", testid }) {
  if (change === null || change === undefined) return null;
  const zero = Math.abs(change) < 0.0005;
  const up = change > 0;
  const arrow = zero ? "●" : up ? "▲" : "▼";
  const sign = zero ? "" : up ? "+" : "−";
  const mag = (Math.abs(change) * 100).toFixed(1);
  let tone = "zero";
  if (!zero) tone = (higherIsBetter ? up : !up) ? "pos" : "neg";
  const styles = {
    pos: { color: "var(--pos-text)", background: "var(--pos-bg)" },
    neg: { color: "var(--neg-text)", background: "var(--neg-bg)" },
    zero: { color: "var(--text-muted)", background: "transparent" },
  }[tone];
  return (
    <span className="inline-flex items-center gap-1 text-[13px] font-medium tnum whitespace-nowrap" data-testid={testid}>
      <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5" style={styles}>{arrow} {sign}{mag}%</span>
      {period && <span className="text-nb-ink2 font-normal">{period}</span>}
    </span>
  );
}

/* 9. RowMenu ------------------------------------------------------------ */
export function RowMenu({ items, testid = "row-menu" }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button aria-label="More actions" data-testid={testid}
          className="w-8 h-8 inline-flex items-center justify-center rounded-md text-nb-ink2 hover:bg-[var(--bg-selected)] transition-colors"
          onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal size={20} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-nb-surface w-48">
        {items.map((it, i) => (
          <DropdownMenuItem key={i} data-testid={it.testid}
            onClick={(e) => { e.stopPropagation(); it.onSelect(); }}
            className="cursor-pointer text-[14px]"
            style={it.destructive ? { color: "var(--neg-text)" } : undefined}>
            {it.icon}{it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* 11. ToolAvatar -------------------------------------------------------- */
export function ToolAvatar({ name = "?", logoUrl, size = 40, testid }) {
  if (logoUrl) {
    return <img src={logoUrl} alt={name} width={size} height={size} className="rounded-[10px] object-contain" style={{ background: "var(--bg-sunken)" }} data-testid={testid} />;
  }
  return (
    <span className="inline-flex items-center justify-center rounded-[10px] font-semibold shrink-0"
      style={{ width: size, height: size, background: "var(--bg-sunken)", color: "var(--brand-navy)", fontSize: size * 0.4 }}
      data-testid={testid}>
      {(name[0] || "?").toUpperCase()}
    </span>
  );
}

/* 10. EmptyState -------------------------------------------------------- */
export function EmptyState({ icon, title, body, action, templates, testid = "empty-state" }) {
  const Icon = icon || Inbox;
  return (
    <div className="bg-nb-surface rounded-2xl shadow-[0_1px_2px_rgba(23,36,63,0.06),0_4px_16px_rgba(23,36,63,0.05)] p-12 text-center" data-testid={testid}>
      <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4" style={{ background: "var(--bg-sunken)" }}>
        <Icon size={24} strokeWidth={1.75} className="text-nb-ink" />
      </span>
      <p className="text-[16px] font-semibold text-nb-ink">{title}</p>
      {body && <p className="text-[14px] text-nb-ink2 mt-2 max-w-[420px] mx-auto">{body}</p>}
      {action && <div className="mt-5 flex justify-center">{action}</div>}
      {templates && <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-[560px] mx-auto">{templates}</div>}
    </div>
  );
}

/* 6. DataTable ---------------------------------------------------------- */
export function DataTable({ columns, rows, getRowKey, onRowClick, defaultSort, testid = "data-table" }) {
  const [sort, setSort] = useState(defaultSort || null);

  const visibleCols = useMemo(
    () => columns.filter((c) => c.always || rows.some((r) => {
      const v = r[c.key];
      return v !== null && v !== undefined && v !== "";
    })),
    [columns, rows]
  );

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const val = col.sortValue || ((r) => r[sort.key]);
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = val(a), bv = val(b);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort, columns]);

  const toggleSort = (key) =>
    setSort((s) => (s?.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  return (
    <div className="bg-nb-surface rounded-2xl shadow-[0_1px_2px_rgba(23,36,63,0.06),0_4px_16px_rgba(23,36,63,0.05)] overflow-hidden" data-testid={testid}>
      <div className="overflow-x-auto">
        <table className="w-full text-[14px] border-collapse">
          <thead>
            <tr style={{ background: "var(--bg-sunken)" }}>
              {visibleCols.map((c, i) => (
                <th key={c.key}
                  onClick={c.sortable === false ? undefined : () => toggleSort(c.key)}
                  className={`nb-label px-4 py-3 whitespace-nowrap ${c.numeric ? "text-right" : "text-left"} ${c.sortable === false ? "" : "cursor-pointer select-none"} ${i === 0 ? "sticky left-0 z-[1]" : ""}`}
                  style={i === 0 ? { background: "var(--bg-sunken)" } : undefined}>
                  <span className={`inline-flex items-center gap-1 ${c.numeric ? "flex-row-reverse" : ""}`}>
                    {c.label}
                    {sort?.key === c.key && (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={getRowKey(r)}
                onClick={onRowClick ? () => onRowClick(r) : undefined}
                data-testid={`row-${getRowKey(r)}`}
                className={`border-t transition-colors ${onRowClick ? "cursor-pointer" : ""} hover:bg-[var(--bg-selected)]`}
                style={{ borderColor: "var(--border-subtle)" }}>
                {visibleCols.map((c, i) => (
                  <td key={c.key}
                    className={`px-4 h-12 ${c.numeric ? "text-right tnum" : "text-left"} ${i === 0 ? "sticky left-0 bg-nb-surface font-semibold text-nb-ink" : "text-nb-ink"}`}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export { ChevronDown };
